import { zodResolver } from "@hookform/resolvers/zod";
import { useRef, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { z } from "zod";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { trpcClient } from "@/lib/trpc";
import { AlertDialogType } from "@/types/alert-dialog";
import { type AlertDialogImperativeHandle, DefaultAlertDialog } from "../DefaultAlertDialog";
import { Alert, AlertDescription, AlertTitle } from "../ui/alert";
import { Field } from "../ui/field";

// Zod validation schema
const deleteTeamSchema = z.object({
    confirmText: z
        .string()
        .min(1, "This field is required")
        .refine((val) => val.toLowerCase() === "delete", {
            message: "Please enter 'DELETE' to confirm",
        }),
});

type DeleteTeamFormData = {
    confirmText: string;
};

export default function DangerSettings() {
    const [isDeleted, setIsDeleted] = useState(false);
    const deleteAlertDialogRef = useRef<AlertDialogImperativeHandle | null>(null);
    const form = useForm<DeleteTeamFormData>({
        resolver: zodResolver(deleteTeamSchema),
        mode: "onChange",
        defaultValues: {
            confirmText: "",
        },
    });

    const onSubmit = async (data: DeleteTeamFormData) => {
        // Open email client when form is valid
        const confirmed = await deleteAlertDialogRef.current!.show();
        if (confirmed) {
            setIsDeleted(true);
            trpcClient.deleteTeam.mutate({ confirmText: data.confirmText });
        } else {
            form.setValue("confirmText", "");
        }
    };

    if (isDeleted) {
        return (
            <Alert variant="destructive">
                <AlertTitle>Scheduled for deletion.</AlertTitle>
                <AlertDescription>
                    <p>
                        Your team has been scheduled for deletion within 48 hours. You will receive an email when it is complete. In the meantime, if
                        you have any questions, please contact{" "}
                        <a className="inline-block" href="mailto:support@orsane.app">
                            support@orsane.app
                        </a>
                        .
                    </p>
                </AlertDescription>
            </Alert>
        );
    }

    const isValid = form.formState.isValid;

    return (
        <>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
                <div className="text-base">
                    <p className="mb-4">
                        Deleting your team will delete all its data, including all tasks and lists. This action cannot be undone and is permanent.
                    </p>
                </div>
                <div>
                    <Controller
                        control={form.control}
                        name="confirmText"
                        render={({ field, fieldState }) => (
                            <div className="w-64">
                                <Field className="gap-1">
                                    <Input
                                        data-1p-ignore
                                        {...field}
                                        placeholder="Enter 'DELETE' to confirm..."
                                        className="w-auto uppercase placeholder:normal-case"
                                    />
                                </Field>
                            </div>
                        )}
                    />
                </div>

                <Button type="submit" variant="destructive" disabled={!isValid}>
                    Delete team
                </Button>
            </form>
            <DefaultAlertDialog
                ref={deleteAlertDialogRef}
                type={AlertDialogType.Delete}
                title="Are you sure?"
                description="Are you sure you want to delete your team? This action cannot be undone and is permanent."
            />
        </>
    );
}
